import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
      return await res.json();
    } catch (err) { return {}; }
  },
  post: async (endpoint, data, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (err) { return { error: err.message }; }
  }
};

function ParentDashboardPage({ token, user }) {
  const { language } = useLanguage();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studentId, setStudentId] = useState('');
  const [activeSection, setActiveSection] = useState('overview');
  const [subjectDetail, setSubjectDetail] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [readingStats, setReadingStats] = useState(null);
  const [readingLoading, setReadingLoading] = useState(false);
  const [monthlyReport, setMonthlyReport] = useState(null);
  
  // Auto-load if user has linked student
  useEffect(() => {
    if (user?.linked_student_id) {
      setStudentId(user.linked_student_id);
      loadDashboard(user.linked_student_id);
    } else {
      setLoading(false);
    }
  }, [user]);
  
  const loadDashboard = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/parent-dashboard/overview/${id}`, token);
      if (res.success) {
        setDashboardData(res);
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
    }
    setLoading(false);
  };
  
  const loadSubjectDetail = async (subject) => {
    try {
      const res = await api.get(`/api/parent-dashboard/subject-details/${studentId}/${subject}`, token);
      if (res.success) {
        setSubjectDetail(res);
        setActiveSection('subject-detail');
      }
    } catch (err) {
      console.error('Subject detail error:', err);
    }
  };
  
  const loadComparison = async () => {
    try {
      const res = await api.get(`/api/parent-dashboard/compare-with-class/${studentId}`, token);
      if (res.success) {
        setComparisonData(res);
        setActiveSection('comparison');
      }
    } catch (err) {
      console.error('Comparison error:', err);
    }
  };

  const loadReadingStats = async () => {
    setReadingLoading(true);
    try {
      const res = await api.get(`/api/library/reading-stats/${studentId}`, token);
      if (res.success) {
        setReadingStats(res);
        setActiveSection('reading');
      }
    } catch (err) {
      console.error('Reading stats error:', err);
    }
    setReadingLoading(false);
  };

  const loadMonthlyReport = async (month) => {
    try {
      const endpoint = month ? `/api/library/monthly-report/${studentId}?month=${month}` : `/api/library/monthly-report/${studentId}`;
      const res = await api.get(endpoint, token);
      if (res.success) setMonthlyReport(res);
    } catch (err) {
      console.error('Monthly report error:', err);
    }
  };

  const downloadCertificate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/library/reading-certificate/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Reading_Certificate_${studentId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Certificate download error:', err);
    }
  };
  
  // If no student ID, show input form
  if (!dashboardData && !loading) {
    return (
      <div className="max-w-lg mx-auto mt-10">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <span className="text-6xl block mb-4">👶</span>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {language === 'hi' ? 'बच्चे की रिपोर्ट देखें' : 'View Child\'s Report'}
          </h2>
          <p className="text-gray-500 mb-6">
            {language === 'hi' ? 'Student ID डालकर complete dashboard देखें' : 'Enter Student ID to view complete dashboard'}
          </p>
          <div className="flex gap-2">
            <input 
              type="text" 
              placeholder={language === 'hi' ? "Student ID" : "Student ID"}
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              className="flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              data-testid="parent-dashboard-student-id"
            />
            <button 
              onClick={() => loadDashboard(studentId)}
              className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition font-medium"
              data-testid="parent-dashboard-load-btn"
            >
              {language === 'hi' ? 'देखें' : 'View'}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }
  
  const { student, attendance, academics, learning_activity, practice_and_challenges, fees, alerts, ai_insight, recent_notices } = dashboardData;
  
  return (
    <div className="space-y-6" data-testid="parent-dashboard">
      {/* Header with Student Info */}
      <div className="bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-3xl">
            {student?.photo ? <img src={student.photo} alt="" className="w-full h-full rounded-full object-cover" /> : '👶'}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{student?.name}</h1>
            <p className="text-white/80">{student?.name_hindi}</p>
            <div className="flex gap-4 mt-1 text-sm">
              <span>🎓 {student?.class}</span>
              <span>📋 Roll: {student?.roll_number}</span>
            </div>
          </div>
          <div className="text-right">
            <button 
              onClick={() => loadDashboard(studentId)}
              className="px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition text-sm"
            >
              🔄 {language === 'hi' ? 'Refresh' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Quick Alerts */}
      {alerts && alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, idx) => (
            <div key={idx} className={`p-4 rounded-xl flex items-center gap-3 ${
              alert.type === 'critical' ? 'bg-red-50 border border-red-200' :
              alert.type === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
              'bg-blue-50 border border-blue-200'
            }`}>
              <span className="text-2xl">{alert.icon}</span>
              <div className="flex-1">
                <p className="font-medium">{alert.message}</p>
                <p className="text-sm text-gray-500">{alert.action}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Navigation Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'overview', label: language === 'hi' ? 'Overview' : 'Overview', icon: '📊' },
          { id: 'attendance', label: language === 'hi' ? 'Attendance' : 'Attendance', icon: '✅' },
          { id: 'academics', label: language === 'hi' ? 'Academics' : 'Academics', icon: '📚' },
          { id: 'learning', label: language === 'hi' ? 'Learning' : 'Learning', icon: '🧠' },
          { id: 'practice', label: language === 'hi' ? 'Practice' : 'Practice', icon: '🎯' },
        ].map(tab => (
          <button 
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`px-4 py-2 rounded-full whitespace-nowrap flex items-center gap-2 transition ${
              activeSection === tab.id 
                ? 'bg-orange-500 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
        <button 
          onClick={loadReadingStats}
          data-testid="reading-tab-btn"
          className={`px-4 py-2 rounded-full whitespace-nowrap flex items-center gap-2 transition ${
            activeSection === 'reading' 
              ? 'bg-teal-500 text-white' 
              : 'bg-white text-gray-600 hover:bg-gray-100'
          }`}
        >
          <span>📖</span>
          <span>{language === 'hi' ? 'पठन / Reading' : 'Reading'}</span>
        </button>
        <button 
          onClick={loadComparison}
          className={`px-4 py-2 rounded-full whitespace-nowrap flex items-center gap-2 transition ${
            activeSection === 'comparison' 
              ? 'bg-purple-500 text-white' 
              : 'bg-white text-gray-600 hover:bg-gray-100'
          }`}
        >
          <span>📈</span>
          <span>{language === 'hi' ? 'Class से Compare' : 'Compare with Class'}</span>
        </button>
      </div>
      
      {/* Content based on active section */}
      {activeSection === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Attendance Card */}
          <div className={`bg-white rounded-xl p-5 shadow-sm border-l-4 ${
            attendance.status === 'good' ? 'border-green-500' : 
            attendance.status === 'warning' ? 'border-yellow-500' : 'border-red-500'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-sm">{language === 'hi' ? 'Attendance' : 'Attendance'}</span>
              <span className={`text-2xl ${attendance.status === 'good' ? '' : 'animate-pulse'}`}>
                {attendance.status === 'good' ? '✅' : attendance.status === 'warning' ? '⚠️' : '🚨'}
              </span>
            </div>
            <p className="text-3xl font-bold">{attendance.percentage}%</p>
            <p className="text-sm text-gray-500 mt-1">{attendance.present}/{attendance.total_days} {language === 'hi' ? 'दिन' : 'days'}</p>
            {attendance.consecutive_absent > 0 && (
              <p className="text-xs text-red-500 mt-2">⚠️ {attendance.consecutive_absent} {language === 'hi' ? 'दिन से absent' : 'days absent'}</p>
            )}
          </div>
          
          {/* Academics Card */}
          <div className={`bg-white rounded-xl p-5 shadow-sm border-l-4 ${
            academics.status === 'excellent' ? 'border-green-500' : 
            academics.status === 'good' ? 'border-blue-500' : 'border-orange-500'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-sm">{language === 'hi' ? 'Overall Marks' : 'Overall Marks'}</span>
              <span className="text-2xl">{academics.overall_grade === 'A+' || academics.overall_grade === 'A' ? '🌟' : '📚'}</span>
            </div>
            <p className="text-3xl font-bold">{academics.overall_percentage}%</p>
            <p className="text-sm text-gray-500 mt-1">Grade: <span className="font-semibold">{academics.overall_grade}</span></p>
            <p className="text-xs text-gray-400 mt-1">{academics.total_exams} {language === 'hi' ? 'exams' : 'exams'}</p>
          </div>
          
          {/* Learning Activity Card */}
          <div className="bg-white rounded-xl p-5 shadow-sm border-l-4 border-purple-500">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-sm">{language === 'hi' ? 'Learning Activity' : 'Learning'}</span>
              <span className="text-2xl">🧠</span>
            </div>
            <p className="text-3xl font-bold">{learning_activity.total_doubts}</p>
            <p className="text-sm text-gray-500 mt-1">{language === 'hi' ? 'सवाल पूछे' : 'Questions asked'}</p>
            <p className="text-xs text-gray-400 mt-1">
              {learning_activity.activity_level === 'high' ? '🔥 Very Active' : 
               learning_activity.activity_level === 'medium' ? '👍 Active' : '💤 Low Activity'}
            </p>
          </div>
          
          {/* Practice Card */}
          <div className="bg-white rounded-xl p-5 shadow-sm border-l-4 border-indigo-500">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-sm">{language === 'hi' ? 'Practice & Challenges' : 'Practice'}</span>
              <span className="text-2xl">🎯</span>
            </div>
            <p className="text-3xl font-bold">{practice_and_challenges.practice_tests.total + practice_and_challenges.weekly_challenges.total}</p>
            <p className="text-sm text-gray-500 mt-1">{language === 'hi' ? 'Tests completed' : 'Tests completed'}</p>
            <p className="text-xs text-gray-400 mt-1">
              🔥 {practice_and_challenges.gamification.current_streak} {language === 'hi' ? 'दिन streak' : 'day streak'}
            </p>
          </div>
        </div>
      )}
      
      {activeSection === 'overview' && (
        <>
          {/* AI Insight Card */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
            <div className="flex items-start gap-4">
              <span className="text-3xl">🤖</span>
              <div>
                <h3 className="font-semibold text-purple-800 mb-2">{language === 'hi' ? 'AI Analysis' : 'AI Analysis'}</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{ai_insight}</p>
              </div>
            </div>
          </div>
          
          {/* Subject Performance */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <span>📚</span> {language === 'hi' ? 'Subject-wise Performance' : 'Subject-wise Performance'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(academics.subject_wise || {}).map(([subject, data]) => (
                <button 
                  key={subject}
                  onClick={() => loadSubjectDetail(subject)}
                  className="p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition text-center"
                >
                  <p className="font-medium text-gray-800">{subject}</p>
                  <p className={`text-2xl font-bold mt-1 ${
                    data.percentage >= 80 ? 'text-green-600' : 
                    data.percentage >= 60 ? 'text-blue-600' : 
                    data.percentage >= 40 ? 'text-yellow-600' : 'text-red-600'
                  }`}>{data.percentage}%</p>
                  <p className="text-xs text-gray-500">Grade: {data.grade}</p>
                </button>
              ))}
            </div>
          </div>
          
          {/* Weak Topics */}
          {learning_activity.weak_topics && learning_activity.weak_topics.length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span>⚠️</span> {language === 'hi' ? 'Focus करने वाले Topics' : 'Topics to Focus'}
              </h3>
              <div className="flex flex-wrap gap-2">
                {learning_activity.weak_topics.map((topic, idx) => (
                  <span key={idx} className="px-4 py-2 bg-red-50 text-red-700 rounded-full text-sm">
                    {topic.topic} <span className="text-xs">({topic.count}x {language === 'hi' ? 'पूछा' : 'asked'})</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Badges */}
          {practice_and_challenges.gamification.badges && practice_and_challenges.gamification.badges.length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span>🏆</span> {language === 'hi' ? 'Earned Badges' : 'Earned Badges'}
              </h3>
              <div className="flex flex-wrap gap-3">
                {practice_and_challenges.gamification.badges.map((badge, idx) => (
                  <div key={idx} className="p-3 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl text-center border border-yellow-200">
                    <span className="text-2xl block">{badge.icon}</span>
                    <span className="text-xs font-medium">{badge.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Attendance Detail Section */}
      {activeSection === 'attendance' && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">{language === 'hi' ? 'Last 7 Days Attendance' : 'Last 7 Days Attendance'}</h3>
          <div className="grid grid-cols-7 gap-2">
            {(attendance.recent || []).map((record, idx) => (
              <div key={idx} className={`p-3 rounded-lg text-center ${
                record.status === 'present' ? 'bg-green-100 text-green-700' :
                record.status === 'absent' ? 'bg-red-100 text-red-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>
                <p className="text-xs">{record.date?.slice(5)}</p>
                <p className="text-lg">{record.status === 'present' ? '✅' : record.status === 'absent' ? '❌' : '⏰'}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-6 grid grid-cols-4 gap-4">
            <div className="p-4 bg-green-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-green-600">{attendance.present}</p>
              <p className="text-sm text-gray-500">{language === 'hi' ? 'उपस्थित' : 'Present'}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-red-600">{attendance.absent}</p>
              <p className="text-sm text-gray-500">{language === 'hi' ? 'अनुपस्थित' : 'Absent'}</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-yellow-600">{attendance.late}</p>
              <p className="text-sm text-gray-500">{language === 'hi' ? 'देर से' : 'Late'}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-blue-600">{attendance.percentage}%</p>
              <p className="text-sm text-gray-500">{language === 'hi' ? 'कुल' : 'Total'}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Academics Detail Section */}
      {activeSection === 'academics' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-800 mb-4">{language === 'hi' ? 'Recent Exam Results' : 'Recent Exam Results'}</h3>
            <div className="space-y-3">
              {(academics.recent_results || []).map((result, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">{result.exam_name || result.subject}</p>
                    <p className="text-sm text-gray-500">{result.subject}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold">{result.marks_obtained}/{result.total_marks}</p>
                    <p className={`text-sm ${
                      (result.marks_obtained / result.total_marks * 100) >= 80 ? 'text-green-600' :
                      (result.marks_obtained / result.total_marks * 100) >= 60 ? 'text-blue-600' : 'text-orange-600'
                    }`}>
                      {Math.round(result.marks_obtained / result.total_marks * 100)}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Learning Detail Section */}
      {activeSection === 'learning' && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">{language === 'hi' ? 'Recent Questions Asked' : 'Recent Questions Asked'}</h3>
          {learning_activity.recent_doubts && learning_activity.recent_doubts.length > 0 ? (
            <div className="space-y-3">
              {learning_activity.recent_doubts.map((doubt, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex gap-2 mb-2">
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">{doubt.subject}</span>
                    <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded text-xs">{doubt.topic}</span>
                  </div>
                  <p className="text-gray-700">{doubt.question}</p>
                  <p className="text-xs text-gray-400 mt-2">{doubt.date?.slice(0, 10)}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">{language === 'hi' ? 'अभी कोई doubt नहीं पूछा' : 'No questions asked yet'}</p>
          )}
        </div>
      )}
      
      {/* Practice Detail Section */}
      {activeSection === 'practice' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-800 mb-4">📝 {language === 'hi' ? 'Practice Tests' : 'Practice Tests'}</h3>
            <div className="text-center mb-4">
              <p className="text-4xl font-bold text-indigo-600">{practice_and_challenges.practice_tests.total}</p>
              <p className="text-gray-500">{language === 'hi' ? 'Tests पूरे किए' : 'Tests completed'}</p>
              <p className="text-sm text-gray-400 mt-1">{language === 'hi' ? 'औसत Score:' : 'Avg Score:'} {practice_and_challenges.practice_tests.average_score}%</p>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-800 mb-4">🎯 {language === 'hi' ? 'Weekly Challenges' : 'Weekly Challenges'}</h3>
            <div className="text-center mb-4">
              <p className="text-4xl font-bold text-purple-600">{practice_and_challenges.weekly_challenges.total}</p>
              <p className="text-gray-500">{language === 'hi' ? 'Challenges पूरे किए' : 'Challenges completed'}</p>
              <p className="text-sm text-gray-400 mt-1">{language === 'hi' ? 'Best Rank:' : 'Best Rank:'} #{practice_and_challenges.weekly_challenges.best_rank || 'N/A'}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Subject Detail Section */}
      {activeSection === 'subject-detail' && subjectDetail && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <button onClick={() => setActiveSection('overview')} className="text-orange-600 mb-4 flex items-center gap-1">
            ← {language === 'hi' ? 'वापस जाएं' : 'Go Back'}
          </button>
          <h3 className="font-semibold text-gray-800 mb-4 text-xl">{subjectDetail.subject} {language === 'hi' ? 'की Details' : 'Details'}</h3>
          
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-blue-600">{subjectDetail.stats.average_percentage}%</p>
              <p className="text-xs text-gray-500">{language === 'hi' ? 'औसत' : 'Average'}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-green-600">{subjectDetail.stats.grade}</p>
              <p className="text-xs text-gray-500">Grade</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-purple-600">{subjectDetail.stats.total_exams}</p>
              <p className="text-xs text-gray-500">{language === 'hi' ? 'Exams' : 'Exams'}</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-xl text-center">
              <p className="text-lg font-bold text-orange-600">
                {subjectDetail.stats.trend === 'improving' ? '📈' : subjectDetail.stats.trend === 'declining' ? '📉' : '➡️'}
              </p>
              <p className="text-xs text-gray-500">{subjectDetail.stats.trend}</p>
            </div>
          </div>
          
          <h4 className="font-medium text-gray-700 mb-3">{language === 'hi' ? 'Exam History' : 'Exam History'}</h4>
          <div className="space-y-2">
            {subjectDetail.exam_history?.map((exam, idx) => (
              <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span>{exam.exam_name}</span>
                <span className="font-bold">{exam.marks}/{exam.total} ({exam.percentage}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Comparison Section */}
      {activeSection === 'comparison' && comparisonData && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <button onClick={() => setActiveSection('overview')} className="text-orange-600 mb-4 flex items-center gap-1">
            ← {language === 'hi' ? 'वापस जाएं' : 'Go Back'}
          </button>
          <h3 className="font-semibold text-gray-800 mb-4 text-xl">📈 {language === 'hi' ? 'Class से Comparison' : 'Class Comparison'}</h3>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Academics Comparison */}
            <div className="p-6 bg-blue-50 rounded-xl">
              <h4 className="font-medium text-blue-800 mb-4">📚 {language === 'hi' ? 'Academics' : 'Academics'}</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>{student?.name}</span>
                  <span className="font-bold text-blue-600">{comparisonData.comparison.academics.student}%</span>
                </div>
                <div className="flex justify-between">
                  <span>{language === 'hi' ? 'Class औसत' : 'Class Average'}</span>
                  <span className="font-bold">{comparisonData.comparison.academics.class_average}%</span>
                </div>
                <div className={`p-3 rounded-lg text-center ${
                  comparisonData.comparison.academics.status === 'above' ? 'bg-green-100 text-green-700' : 
                  comparisonData.comparison.academics.status === 'below' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100'
                }`}>
                  {comparisonData.comparison.academics.status === 'above' ? '🌟' : comparisonData.comparison.academics.status === 'below' ? '💪' : '✅'}
                  {comparisonData.comparison.academics.difference > 0 ? '+' : ''}{comparisonData.comparison.academics.difference}%
                </div>
              </div>
            </div>
            
            {/* Attendance Comparison */}
            <div className="p-6 bg-green-50 rounded-xl">
              <h4 className="font-medium text-green-800 mb-4">✅ {language === 'hi' ? 'Attendance' : 'Attendance'}</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>{student?.name}</span>
                  <span className="font-bold text-green-600">{comparisonData.comparison.attendance.student}%</span>
                </div>
                <div className="flex justify-between">
                  <span>{language === 'hi' ? 'Class औसत' : 'Class Average'}</span>
                  <span className="font-bold">{comparisonData.comparison.attendance.class_average}%</span>
                </div>
                <div className={`p-3 rounded-lg text-center ${
                  comparisonData.comparison.attendance.status === 'above' ? 'bg-green-100 text-green-700' : 
                  comparisonData.comparison.attendance.status === 'below' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100'
                }`}>
                  {comparisonData.comparison.attendance.status === 'above' ? '🌟' : comparisonData.comparison.attendance.status === 'below' ? '💪' : '✅'}
                  {comparisonData.comparison.attendance.difference > 0 ? '+' : ''}{comparisonData.comparison.attendance.difference}%
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-purple-50 rounded-xl text-center">
            <p className="text-lg">{comparisonData.insights}</p>
            <p className="text-sm text-gray-500 mt-2">{language === 'hi' ? 'Class Size:' : 'Class Size:'} {comparisonData.class_size} students</p>
          </div>
        </div>
      )}
      
      {/* Reading Section */}
      {activeSection === 'reading' && (
        <div className="space-y-4" data-testid="reading-section">
          {readingLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
            </div>
          ) : readingStats ? (
            <>
              {/* Reading Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-teal-500">
                  <p className="text-xs text-gray-500">{language === 'hi' ? 'कुल पुस्तकें पढ़ीं' : 'Total Books Read'}</p>
                  <p className="text-2xl font-bold text-teal-600" data-testid="total-books-read">{readingStats.stats.total_returned}</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-blue-500">
                  <p className="text-xs text-gray-500">{language === 'hi' ? 'अभी पढ़ रहे हैं' : 'Currently Reading'}</p>
                  <p className="text-2xl font-bold text-blue-600">{readingStats.stats.currently_reading}</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-purple-500">
                  <p className="text-xs text-gray-500">{language === 'hi' ? 'Class Rank' : 'Class Rank'}</p>
                  <p className="text-2xl font-bold text-purple-600">#{readingStats.stats.class_rank || '-'}</p>
                  <p className="text-xs text-gray-400">{readingStats.stats.total_readers} {language === 'hi' ? 'में से' : 'of'}</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-orange-500">
                  <p className="text-xs text-gray-500">{language === 'hi' ? 'कुल जुर्माना' : 'Total Fines'}</p>
                  <p className="text-2xl font-bold text-orange-600">₹{readingStats.stats.total_fines}</p>
                </div>
              </div>

              {/* Milestones */}
              <div className="bg-white rounded-xl p-5 shadow-sm">
                <h3 className="font-semibold text-gray-800 mb-3">{language === 'hi' ? 'पठन उपलब्धियाँ' : 'Reading Milestones'}</h3>
                <div className="flex flex-wrap gap-3" data-testid="milestones-container">
                  {readingStats.milestones.map((m, i) => (
                    <div key={i} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                      m.achieved 
                        ? 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 border border-amber-300' 
                        : 'bg-gray-100 text-gray-400 border border-gray-200'
                    }`}>
                      <span className="text-lg">{m.emoji}</span>
                      <span>{m.badge_hi}</span>
                      <span className="text-xs">({m.count})</span>
                      {m.achieved && <span className="text-green-500">✓</span>}
                    </div>
                  ))}
                </div>
                {readingStats.next_milestone && (
                  <div className="mt-3 p-3 bg-teal-50 rounded-lg">
                    <p className="text-sm text-teal-700">
                      {language === 'hi' 
                        ? `अगली उपलब्धि: ${readingStats.next_milestone.badge_hi} - ${readingStats.next_milestone.remaining} और किताबें पढ़ें!`
                        : `Next milestone: ${readingStats.next_milestone.remaining} more books to go!`}
                    </p>
                    <div className="w-full bg-teal-200 rounded-full h-2 mt-2">
                      <div 
                        className="bg-teal-500 h-2 rounded-full transition-all"
                        style={{ width: `${((readingStats.next_milestone.count - readingStats.next_milestone.remaining) / readingStats.next_milestone.count) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Certificate Download */}
              {readingStats.stats.can_get_certificate && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5 shadow-sm border border-amber-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-amber-800 text-lg">🎓 {language === 'hi' ? 'Reading Certificate तैयार है!' : 'Reading Certificate Ready!'}</h3>
                      <p className="text-sm text-amber-600">{language === 'hi' ? `${readingStats.stats.total_returned} किताबें पढ़ने का प्रमाण पत्र डाउनलोड करें` : `Download certificate for reading ${readingStats.stats.total_returned} books`}</p>
                    </div>
                    <button
                      onClick={downloadCertificate}
                      data-testid="download-certificate-btn"
                      className="px-5 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition font-medium flex items-center gap-2"
                    >
                      📄 {language === 'hi' ? 'Download PDF' : 'Download PDF'}
                    </button>
                  </div>
                </div>
              )}

              {/* Monthly Reading Chart */}
              {readingStats.monthly_breakdown && readingStats.monthly_breakdown.length > 0 && (
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <h3 className="font-semibold text-gray-800 mb-3">{language === 'hi' ? 'मासिक पठन प्रगति' : 'Monthly Reading Progress'}</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={readingStats.monthly_breakdown}>
                      <XAxis dataKey="month_name" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="books_read" fill="#14b8a6" radius={[4, 4, 0, 0]} name={language === 'hi' ? 'किताबें' : 'Books'} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Currently Reading */}
              {readingStats.currently_reading && readingStats.currently_reading.length > 0 && (
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <h3 className="font-semibold text-gray-800 mb-3">{language === 'hi' ? 'अभी पढ़ रहे हैं' : 'Currently Reading'}</h3>
                  <div className="space-y-2">
                    {readingStats.currently_reading.map((t, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-800">{t.book_title}</p>
                          <p className="text-xs text-gray-500">{language === 'hi' ? 'जारी:' : 'Issued:'} {new Date(t.issue_date).toLocaleDateString('hi-IN')}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-red-500 font-medium">{language === 'hi' ? 'वापसी:' : 'Due:'} {new Date(t.due_date).toLocaleDateString('hi-IN')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Favorite Categories */}
              {readingStats.favorite_categories && readingStats.favorite_categories.length > 0 && (
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <h3 className="font-semibold text-gray-800 mb-3">{language === 'hi' ? 'पसंदीदा विषय' : 'Favorite Categories'}</h3>
                  <div className="flex flex-wrap gap-2">
                    {readingStats.favorite_categories.map((c, i) => (
                      <span key={i} className="px-3 py-1.5 bg-teal-50 text-teal-700 rounded-full text-sm font-medium border border-teal-200">
                        {c.category} ({c.count})
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              {readingStats.recent_activity && readingStats.recent_activity.length > 0 && (
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <h3 className="font-semibold text-gray-800 mb-3">{language === 'hi' ? 'हाल की गतिविधि' : 'Recent Activity'}</h3>
                  <div className="space-y-2">
                    {readingStats.recent_activity.slice(0, 8).map((t, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                        <span className={`text-lg ${t.status === 'issued' ? 'text-blue-500' : 'text-green-500'}`}>
                          {t.status === 'issued' ? '📗' : '📘'}
                        </span>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-700">{t.book_title}</p>
                          <p className="text-xs text-gray-400">
                            {t.status === 'issued' 
                              ? `${language === 'hi' ? 'जारी' : 'Issued'}: ${new Date(t.issue_date).toLocaleDateString('hi-IN')}`
                              : `${language === 'hi' ? 'वापस' : 'Returned'}: ${new Date(t.return_date).toLocaleDateString('hi-IN')}`}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${t.status === 'issued' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {t.status === 'issued' ? (language === 'hi' ? 'पढ़ रहे हैं' : 'Reading') : (language === 'hi' ? 'पूर्ण' : 'Done')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl p-8 shadow-sm text-center">
              <span className="text-5xl block mb-3">📖</span>
              <p className="text-gray-500">{language === 'hi' ? 'पठन डेटा लोड हो रहा है...' : 'Loading reading data...'}</p>
            </div>
          )}
        </div>
      )}
      
      {/* Fee Status */}
      {activeSection === 'overview' && fees && (
        <div className={`bg-white rounded-xl p-6 shadow-sm border-l-4 ${
          fees.status === 'clear' ? 'border-green-500' : 
          fees.status === 'overdue' ? 'border-red-500' : 'border-yellow-500'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-800">{language === 'hi' ? 'Fee Status' : 'Fee Status'}</h3>
              <p className="text-gray-500 text-sm">
                {fees.status === 'clear' 
                  ? (language === 'hi' ? '✅ सभी fees paid' : '✅ All fees paid')
                  : (language === 'hi' ? `₹${fees.total_pending} pending` : `₹${fees.total_pending} pending`)
                }
              </p>
            </div>
            {fees.total_pending > 0 && (
              <span className="text-2xl font-bold text-orange-600">₹{fees.total_pending}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// AI Features Page

export default ParentDashboardPage;
